import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Trash2, Mic } from "lucide-react";
import VoiceBookletButton from "@/components/VoiceBookletButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KINDS = [
  ["all", "Toutes"],
  ["story", "Lectures de contes"],
  ["message", "Mots doux"],
  ["song", "Chansons"],
  ["reply", "Réponses du patient"],
];
const KIND_LABEL = { story: "Lecture de conte", message: "Mot doux", song: "Chanson", reply: "Réponse du patient" };

function VoiceRow({ v, onDelete }) {
  const what = v.kind === "story" ? v.story_title : v.kind === "song" ? v.song_title : "";
  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid={`voice-row-${v.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-800 truncate">
            {KIND_LABEL[v.kind] || "Voix"}{what ? ` · ${what}` : ""}
          </p>
          <p className="text-xs text-gray-500">Par {v.label} · {new Date(v.created_at).toLocaleDateString("fr-FR")}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-lg">
            <a href={`${BACKEND}${v.audio_url}`} download data-testid={`voice-download-${v.id}`}><Download className="w-4 h-4" /></a>
          </Button>
          <Button variant="outline" size="sm" className="rounded-lg text-red-600" onClick={() => onDelete(v)} data-testid={`voice-delete-${v.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <audio controls preload="none" src={`${BACKEND}${v.audio_url}`} className="w-full h-10" data-testid={`voice-audio-${v.id}`} />
    </div>
  );
}

export default function VoicesLibrary({ patient }) {
  const [voices, setVoices] = useState([]);
  const [kind, setKind] = useState("all");
  const load = () => api.get(`/patients/${patient.id}/voices`).then((r) => setVoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, [patient.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (v) => {
    if (!window.confirm("Supprimer définitivement cet enregistrement ?")) return;
    try { await api.delete(`/patients/${patient.id}/recordings/${v.id}`); toast.success("Enregistrement supprimé"); load(); }
    catch { toast.error("Suppression impossible."); }
  };
  const list = voices.filter((v) => kind === "all" || v.kind === kind);
  return (
    <Card className="border-gray-200 mt-8" data-testid="voices-library">
      <CardContent className="p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><Mic className="w-4 h-4 text-rose-500" /> Voix enregistrées</h3>
        <p className="text-sm text-gray-500 mb-4">Lectures de contes, mots doux et chansons des proches, et réponses de {patient.name}.</p>
        <VoiceBookletButton url={`/patients/${patient.id}/voice-booklet/pdf`} name={patient.name} testId="caregiver-voice-booklet" className="mb-4" />
        <div className="flex flex-wrap gap-2 mb-4">
          {KINDS.map(([id, label]) => {
            const n = id === "all" ? voices.length : voices.filter((v) => v.kind === id).length;
            return (
              <button key={id} onClick={() => setKind(id)} data-testid={`voices-filter-${id}`} aria-pressed={kind === id}
                className={`rounded-full px-4 h-9 text-sm font-medium border transition-colors ${kind === id ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                {label} ({n})
              </button>
            );
          })}
        </div>
        {list.length === 0 ? (
          <p className="text-gray-400 py-6 text-center" data-testid="voices-empty">Aucun enregistrement pour le moment.</p>
        ) : (
          <div className="space-y-3" data-testid="voices-list">{list.map((v) => <VoiceRow key={v.id} v={v} onDelete={remove} />)}</div>
        )}
      </CardContent>
    </Card>
  );
}
